import React, { FC } from 'react'
import { socialImpactStrings } from 'common/strings/social-impact-strings'
import classNames from 'classnames'
import Icon from 'components/Icon'
import useWindowSize from 'components/util/useWindowSize'
import imageResize from 'project/image-proxy-web'

type CommunityGoalsCardType = {
  heroTitle: string
  heroSubtitle: string
  title: string
  subtitle?: string
  subtitleClasses?: string
  description?: string
  descriptionClasses?: string
  option?: 1 | 2 | 3
  className?: string
}

const CommunityGoalsCard: FC<CommunityGoalsCardType> = ({
  heroSubtitle,
  heroTitle,
  option = 1,
  subtitle,
  subtitleClasses = 'w-75 mb-xxxl-24 mb-xl-18 mb-lg-9 mb-sm-6 mb-1',
  description = socialImpactStrings.theVisionDescription,
  descriptionClasses = option !== 2 ? 'mb-4' : 'mb-md-22 mb-8',
  title,
  className = option !== 2 ? 'pt-6' : 'pt-4',
}) => {
  const size = useWindowSize()
  return (
    <div
      className={classNames(
        'position-relative overflow-hidden bg-black text-white text-center pt-lg-31 pt-md-14',
        className,
      )}
    >
      <div
        className={classNames(
          !size.isMdOrLower ? 'bg-radial-gradient-purple' : '',
          { 'bg-radial-gradient-purple--option3': option === 3 },
        )}
      />
      <div className='container'>
        {size.isMdOrLower && (
          <>
            <h1
              className='fs-fluid-24-32 mb-1 text-semibold'
              dangerouslySetInnerHTML={{ __html: `${heroTitle}` }}
            />
            <p
              className={'text-xl-2 text-semibold mb-1'}
              dangerouslySetInnerHTML={{ __html: `${heroSubtitle}` }}
            />
            <Icon
              className={'mt-2'}
              name={'arrow-down'}
              width={20}
              height={29}
            />
          </>
        )}
        <h2
          className={classNames(
            'text-xxxl text-xxxl--large mt-md-0 mb-md-6 mb-2 position-relative',
            option === 1 ? 'mt-14' : 'mt-18',
          )}
        >
          {title}
        </h2>
        <div className={classNames('row', descriptionClasses)}>
          <div className='col-xxxl-10 col-xxl-9 col-12 mx-auto'>
            <h3
              dangerouslySetInnerHTML={{
                __html: description,
              }}
              className={classNames(
                'position-relative z-index-1',
                size.isXlOrHigher
                  ? 'h1 h1--small'
                  : !size.isSmOrLower
                  ? 'h2'
                  : option === 1 || option === 3
                  ? 'text-xl text-bold'
                  : 'h4--small h4--ls2 mx-n4 mb-10',
              )}
            />
          </div>
        </div>
      </div>
      <div
        className={classNames(
          size.isMdOrLower
            ? `bg-radial-gradient-purple bg-radial-gradient-purple2 bg-radial-gradient-purple2--option${option}`
            : '',
        )}
      />
      {option === 1 && (
        <>
          <img
            loading='lazy'
            className={classNames(
              'h-auto d-block mx-auto position-relative',
              size.isSmOrLower ? 'img-fluid' : 'w-85',
            )}
            src={imageResize(
              require('public/images/banking/internation-page-plugz-head-desktop.png'),
            )}
            alt={'Plugz head'}
          />
          {subtitle && (
            <p
              className={classNames(
                'mx-auto position-absolute bottom-0 start-0 end-0 text-bold',
                subtitleClasses,
                size.isXxlOrHigher
                  ? 'text-xxxl text-xxxl--large'
                  : 'lead fs-fluid-18-24',
              )}
              dangerouslySetInnerHTML={{
                __html: subtitle,
              }}
            />
          )}
        </>
      )}
      {option === 2 && (
        <img
          loading='lazy'
          className={classNames(
            'h-auto d-block ms-xxl-56 ms-xl-42 ms-lg-30 ms-sm-auto ms-0 mx-auto position-relative',
            size.isSmOrLower ? '' : 'w-65',
          )}
          src={imageResize(
            !size.isSmOrLower
              ? require('public/images/banking/plans-page-plugz-thumbs-up-desktop.png')
              : require('public/images/banking/plans-page-plugz-thumbs-up-mobile.png'),
          )}
          alt={'Plugz thumbs up'}
        />
      )}
      {option === 3 && (
        <img
          loading='lazy'
          className={classNames(
            'h-auto d-block mx-auto position-relative',
            size.isSmOrLower ? 'w-50 mt-n9 mb-n2 ms-sm-0 ms-24' : 'w-27',
          )}
          src={imageResize(
            require('public/images/plans/forward-page-point-up-hand-desktop.png'),
          )}
          alt={'Plugz hand up'}
        />
      )}
    </div>
  )
}

export default CommunityGoalsCard
